const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/soh/eCheckin/initialValues.ts';
import { usePatientData } from 'App/store/patient';
import { useSelector } from 'react-redux';
import { selectUserDetailsFromRegisterWorkItem } from 'App/store/login';
import { useRelatives } from 'App/store/relative';
import { normalizePhoneNumber } from 'App/utils/phoneNumberUtils';
import { selectRelationTypes } from 'App/store/metaKat';
import { useCaseData } from 'App/store/case';
import { getArztDataFromCase } from 'App/utils/cases';
import { findRelationKey } from 'App/utils/person';
import _ from 'lodash';

export const useECheckinInitialValues = (fallNr?: string) => {
    const [pat, patientIsLoading] = usePatientData();
    const loginDetails = useSelector(selectUserDetailsFromRegisterWorkItem);
    const [caseData, caseDataIsLoading] = fallNr ? useCaseData(fallNr) : [undefined, false];
    const hausArztData = getArztDataFromCase(caseData, 'HausAz');
    const einwArztData = getArztDataFromCase(caseData, 'EinwAz');
    const relationTypes = useSelector(selectRelationTypes);

    const { data: referencePersons, isPendingOrLoading: isRelativesLoading } = fallNr
        ? useRelatives(fallNr)
        : { data: undefined, isPendingOrLoading: false };

    const isLoading = patientIsLoading || caseDataIsLoading || isRelativesLoading;

    const persAngaben = {
        firstname: pat.get('VORNAME') ?? loginDetails?.get('firstname') ?? '',
        lastname: pat.get('NAME') ?? loginDetails?.get('lastname') ?? '',
        maidenname: pat.get('GEBURTSNAME') ?? '',
        streetAndNumber: pat.get('STRASSE') ?? '',
        adressZusatz: pat.get('ANSCHR2') ?? '',
        plz: pat.get('PLZ') ?? '',
        ort: pat.get('ORT') ?? '',
        country: pat.get('LAND') ?? '',
        gender: pat.get('SEX') ?? '',
        dateOfBirth: (pat.get('GEBDAT') || '').slice(0, 10), // YYYY-MM-DD
        tel: normalizePhoneNumber(pat.get('TELEFON') || pat.get('TELEFON_G')) || '',
        mobile: normalizePhoneNumber(pat.get('TELEFON_M') ?? loginDetails?.get('mobile')) ?? '',
        email: pat.get('EMAIL') ?? loginDetails?.get('mail') ?? '',
        nationality: pat.get('NATIONALITAET') ?? '',
        zivilstand: pat.get('ZIVILSTAND') ?? '',
        language: pat.get('SPRACHE')?.toUpperCase() ?? '',
        ahv: (pat.get('SOZVERSNR') as string)?.replace(/\./g, '') ?? '',
        religion: pat.get('KONFESSION') ?? '',
        profession: pat.get('BERUF') ?? '',
    };

    const versicherungUndZusatzleistung = {
        socialNumber: (pat.get('SOZVERSNR') as string)?.replace(/\./g, '') ?? '',
    };

    let kontakt = {};

    const relatives = _.compact(referencePersons);

    if (relatives && relatives.length > 0 && !isRelativesLoading) {
        if (relatives.length >= 2) {
            kontakt = {
                beziehung: findRelationKey(relationTypes, relatives[0].VERWANDSCHAFTSGRAD),
                firstname: relatives[0].VORNAME,
                lastname: relatives[0].NAME,
                tel: normalizePhoneNumber(relatives[0].TEL),
                streetAndNumber: relatives[0].STRASSE,
                ort: relatives[0].ORT,
                plz: relatives[0].PLZ,
                country: relatives[0].LAND,

                secondbeziehung: findRelationKey(relationTypes, relatives[1].VERWANDSCHAFTSGRAD),
                secondfirstname: relatives[1].VORNAME,
                secondlastname: relatives[1].NAME,
                secondtel: normalizePhoneNumber(relatives[1].TEL),
                secondstreetAndNumber: relatives[1].STRASSE,
                secondort: relatives[1].ORT,
                secondplz: relatives[1].PLZ,
                secondcountry: relatives[1].LAND,
            };
        } else {
            kontakt = {
                beziehung: findRelationKey(relationTypes, relatives[0].VERWANDSCHAFTSGRAD),
                firstname: relatives[0].VORNAME,
                lastname: relatives[0].NAME,
                tel: normalizePhoneNumber(relatives[0].TEL),
                streetAndNumber: relatives[0].STRASSE,
                ort: relatives[0].ORT,
                plz: relatives[0].PLZ,
                country: relatives[0].LAND,
            };
        }
    }

    const hausArzt = hausArztData
        ? {
              lastname: hausArztData.Name,
              adresszusatz: hausArztData.Zusatz,
              ort: hausArztData.Ort,
              country: hausArztData.Land,
              firstname: hausArztData.Vorname,
              streetAndNumber: hausArztData.Strasse,
              plz: hausArztData.PLZ,
          }
        : {};

    const zuweisenderArzt = einwArztData
        ? {
              refferedDoctor: 'anderer',
              firstname: einwArztData.Vorname,
              lastname: einwArztData.Name,
              streetAndNumber: einwArztData.Strasse,
              tel: normalizePhoneNumber(einwArztData.Tel),
              ort: einwArztData.Ort,
              plz: einwArztData.PLZ,
              country: einwArztData.Land,
          }
        : {};

    return [{ persAngaben, versicherungUndZusatzleistung, hausArzt, zuweisenderArzt, kontakt }, isLoading];
};
